import apiService from './api'
import type { ExportRequest, BulkExportRequest, ExportFormat, PDFReportConfig, CSVExportConfig } from '@/types/export'

class ExportService {
  /**
   * Export a single scan with specified options
   */
  async exportScan(
    scanId: string, 
    options: {
      format: ExportFormat
      include_findings?: boolean
      include_summary?: boolean
      include_risk_score?: boolean
      include_recommendations?: boolean
      filter_severity?: string[]
      pdf_config?: PDFReportConfig
      csv_config?: CSVExportConfig
    }
  ): Promise<void> {
    const queryParams = new URLSearchParams()
    
    queryParams.append('include_findings', (options.include_findings ?? true).toString())
    queryParams.append('include_summary', (options.include_summary ?? true).toString())
    queryParams.append('include_risk_score', (options.include_risk_score ?? true).toString())
    queryParams.append('include_recommendations', (options.include_recommendations ?? true).toString())
    
    if (options.filter_severity?.length) {
      queryParams.append('filter_severity', options.filter_severity.join(','))
    }
    
    const filename = `risklens_scan_${scanId}.${options.format}`
    await apiService.download(`/scans/${scanId}/export/${options.format}?${queryParams.toString()}`, filename)
  }

  /**
   * Export a single scan with advanced configuration
   */
  async exportScanWithConfig(
    scanId: string,
    exportRequest: ExportRequest,
    pdfConfig?: PDFReportConfig,
    csvConfig?: CSVExportConfig
  ): Promise<void> {
    const body: any = { ...exportRequest }
    
    if (pdfConfig && exportRequest.format === 'pdf') {
      body.pdf_config = pdfConfig
    }
    
    if (csvConfig && exportRequest.format === 'csv') {
      body.csv_config = csvConfig
    }
    
    const response = await apiService.getClient().post(`/scans/${scanId}/export`, body, {
      responseType: 'blob',
    })
    
    // Extract filename from response headers
    const contentDisposition = response.headers['content-disposition']
    const filenameMatch = contentDisposition?.match(/filename="(.+)"/)
    const filename = filenameMatch ? filenameMatch[1] : `risklens_scan_${scanId}.${exportRequest.format}`
    
    // Create download
    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }

  /**
   * Export multiple scans in bulk
   */
  async bulkExportScans(
    bulkRequest: BulkExportRequest
  ): Promise<void> {
    const response = await apiService.getClient().post('/scans/bulk-export', bulkRequest, {
      responseType: 'blob',
    })
    
    // Extract filename from response headers
    const contentDisposition = response.headers['content-disposition']
    const filenameMatch = contentDisposition?.match(/filename="(.+)"/)
    const filename = filenameMatch ? filenameMatch[1] : `risklens_bulk_export.${bulkRequest.format}`
    
    // Create download
    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }

  /**
   * Get export information for a scan
   */
  async getExportInfo(scanId: string): Promise<{
    scan_metadata: any
    export_stats: any
    risk_score_available: boolean
    available_formats: string[]
    supported_severity_filters: string[]
    estimated_file_sizes: Record<string, string>
  }> {
    const response = await apiService.get(`/scans/${scanId}/export-info`)
    return response as {
      scan_metadata: any
      export_stats: any
      risk_score_available: boolean
      available_formats: string[]
      supported_severity_filters: string[]
      estimated_file_sizes: Record<string, string>
    }
  }

  /**
   * Get supported export formats and their capabilities
   */
  async getSupportedFormats(): Promise<{
    formats: Record<string, any>
    configuration_options: Record<string, any>
  }> {
    const response = await apiService.get('/export-formats')
    return response as {
      formats: Record<string, any>
      configuration_options: Record<string, any>
    }
  }

  /**
   * Quick export with sensible defaults
   */
  async quickExport(scanId: string, format: ExportFormat): Promise<void> {
    return this.exportScan(scanId, {
      format,
      include_findings: true,
      include_summary: true,
      include_risk_score: true,
      include_recommendations: true
    })
  }
}

export const exportService = new ExportService()
export default exportService